import { type EoReader, PacketAction, PacketFamily } from 'eolib';
import type { Client } from '@/client';
import { GameState } from '@/game-state';

type TimedSkill = { id: number; activeUntil: number; readyAt: number };
const stores = new WeakMap<Client, Map<number, TimedSkill>>();
const stateListeners = new WeakMap<Client, (state: GameState) => void>();
export function skillStatus(client: Client, id: number) {
  const s = stores.get(client)?.get(id), now = performance.now();
  return { active: Math.max(0, (s?.activeUntil ?? 0) - now), cooldown: Math.max(0, (s?.readyAt ?? 0) - now) };
}
// Recipient snapshots may contain buffs the player has never learned or equipped.
// Keep this independent of the hotbar and include active effects, not cooldowns.
export function activeSkillStatuses(client: Client) {
  const now = performance.now();
  return [...(stores.get(client)?.values() ?? [])]
    .filter(skill => skill.activeUntil > now)
    .map(skill => ({ id: skill.id, active: skill.activeUntil - now }))
    .sort((a, b) => a.id - b.id);
}
export function registerSkillStatusHandlers(client: Client): void {
  stores.set(client, new Map());
  const previous = stateListeners.get(client);
  if (previous) client.unsubscribeStateChanged(previous);
  const clearOutsideGame = (state: GameState) => {
    // Disconnect, logout and character selection invalidate the recipient.
    // Normal map warps stay InGame, so server-owned buffs continue counting down.
    if (state !== GameState.InGame) stores.set(client, new Map());
  };
  stateListeners.set(client, clearOutsideGame);
  client.subscribeStateChanged(clearOutsideGame);
  client.bus!.registerPacketHandler(56 as PacketFamily, PacketAction.Reply, (reader: EoReader) => {
    try {
      const data = JSON.parse(reader.getString()), now = performance.now();
      if (!Array.isArray(data.skills) || data.skills.length > 64) return;
      const next = new Map<number, TimedSkill>();
      for (const s of data.skills) {
        if (!Number.isInteger(s.id) || s.id <= 0 || !Number.isFinite(s.active_ms) || !Number.isFinite(s.cooldown_ms)) return;
        next.set(s.id, { id: s.id, activeUntil: now + Math.max(0, Math.min(s.active_ms, 300000)), readyAt: now + Math.max(0, Math.min(s.cooldown_ms, 300000)) });
      }
      stores.set(client, next);
    } catch { /* Optional status metadata cannot interrupt gameplay. */ }
  });
}
